import { useMemo } from "react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { useLegalPages, type LegalPageKey } from "@/hooks/useLegalPages";

type LegalType = "mentions-legales" | "politique-confidentialite" | "cgv";

interface LegalProps {
  type: LegalType;
}

interface LegalContent {
  title: string;
  h1: string;
  description: string;
  canonical: string;
  sections: { title: string; content: string }[];
}

// Contenu statique = fallback. Surchargé par la collection `legal_pages` quand un override admin existe.
// Le contenu est désormais en HTML pour permettre l'édition en RichTextEditor.
const STATIC_CONTENT: Record<LegalType, LegalContent> = {
  "mentions-legales": {
    title: "Mentions légales — Emplois BNSSA & MNS",
    h1: "Mentions Légales",
    description:
      "Mentions légales du site Emplois BNSSA & MNS : éditeur, hébergeur, propriété intellectuelle et responsabilité.",
    canonical: "https://bnssa-mns.fr/mentions-legales",
    sections: [
      {
        title: "Éditeur du site",
        content:
          "<p>Le site <strong>Emplois BNSSA &amp; MNS</strong> est édité par la société Emplois BNSSA &amp; MNS SAS, au capital de 10 000 €, immatriculée au RCS de Paris sous le numéro 900 000 000. Siège social : 10 rue des Piscines, 75001 Paris, France. Email : contact@bnssa-mns.fr. Téléphone : 01 00 00 00 00.</p>",
      },
      {
        title: "Directeur de la publication",
        content:
          "<p>Le directeur de la publication est M. Le Président de la société Emplois BNSSA &amp; MNS SAS.</p>",
      },
      {
        title: "Hébergement",
        content:
          "<p>Le site est hébergé par OVH SAS, 2 rue Kellermann, 59100 Roubaix, France. Téléphone : 09 72 10 10 07.</p>",
      },
      {
        title: "Propriété intellectuelle",
        content:
          "<p>L'ensemble du contenu du site (textes, images, logos, vidéos) est la propriété exclusive d'Emplois BNSSA &amp; MNS ou de ses partenaires. Toute reproduction, totale ou partielle, est strictement interdite sans autorisation écrite préalable.</p>",
      },
      {
        title: "Responsabilité",
        content:
          "<p>Emplois BNSSA &amp; MNS s'efforce d'assurer l'exactitude des informations diffusées sur le site. Toutefois, l'éditeur ne saurait être tenu responsable des erreurs, omissions ou résultats obtenus à partir de l'utilisation des informations.</p>",
      },
    ],
  },
  "politique-confidentialite": {
    title: "Politique de confidentialité — Emplois BNSSA & MNS",
    h1: "Politique de Confidentialité",
    description:
      "Politique de confidentialité et protection des données personnelles du site Emplois BNSSA & MNS (RGPD).",
    canonical: "https://bnssa-mns.fr/politique-confidentialite",
    sections: [
      {
        title: "Collecte des données personnelles",
        content:
          "<p>Nous collectons des données personnelles lors de votre inscription (nom, email, téléphone, ville, diplômes), lors de vos candidatures (CV, lettre de motivation) et lors de votre navigation (cookies, adresse IP). Ces données sont strictement nécessaires à la fourniture du service.</p>",
      },
      {
        title: "Finalité du traitement",
        content:
          "<p>Vos données sont utilisées pour : créer et gérer votre compte, mettre en relation candidats et recruteurs, personnaliser les offres proposées, envoyer des notifications et alertes emploi, améliorer nos services.</p>",
      },
      {
        title: "Vos droits (RGPD)",
        content:
          "<p>Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité, de limitation et d'opposition concernant vos données personnelles. Pour exercer ces droits, contactez : <a href=\"mailto:dpo@bnssa-mns.fr\">dpo@bnssa-mns.fr</a>.</p>",
      },
      {
        title: "Cookies",
        content:
          "<p>Le site utilise des cookies techniques et analytiques. Vous pouvez à tout moment désactiver les cookies via les paramètres de votre navigateur. Certains cookies sont indispensables au fonctionnement du site.</p>",
      },
      {
        title: "Conservation des données",
        content:
          "<p>Vos données sont conservées pendant la durée de votre inscription et jusqu'à 3 ans après votre dernière connexion. Au-delà, elles sont anonymisées ou supprimées.</p>",
      },
    ],
  },
  "cgv": {
    title: "CGU / CGV — Emplois BNSSA & MNS",
    h1: "Conditions Générales d'Utilisation et de Vente",
    description:
      "Conditions Générales d'Utilisation et de Vente du site Emplois BNSSA & MNS : inscription, obligations, tarifs et droit applicable.",
    canonical: "https://bnssa-mns.fr/cgv",
    sections: [
      {
        title: "Objet",
        content:
          "<p>Les présentes CGU/CGV régissent l'utilisation du site Emplois BNSSA &amp; MNS, plateforme de mise en relation entre candidats (BNSSA, MNS) et recruteurs du secteur des activités aquatiques.</p>",
      },
      {
        title: "Inscription et compte",
        content:
          "<p>L'inscription sur le site est gratuite pour les candidats. Les recruteurs peuvent publier leur première offre gratuitement, puis souscrire à un abonnement pour les offres suivantes. Chaque utilisateur s'engage à fournir des informations exactes et à jour.</p>",
      },
      {
        title: "Obligations du candidat",
        content:
          "<p>Le candidat s'engage à fournir des informations véridiques sur son profil, ses diplômes et son expérience. La fourniture de fausses informations peut entraîner la suspension immédiate du compte.</p>",
      },
      {
        title: "Obligations du recruteur",
        content:
          "<p>Le recruteur s'engage à publier des offres réelles, conformes au droit du travail et à la réglementation des métiers aquatiques. Toute offre discriminatoire ou frauduleuse sera supprimée.</p>",
      },
      {
        title: "Tarifs",
        content:
          "<p>Les tarifs des offres premium pour recruteurs sont indiqués sur la page tarifs et facturés en euros TTC. Les paiements sont sécurisés. La première offre est offerte aux nouveaux recruteurs.</p>",
      },
      {
        title: "Limitation de responsabilité",
        content:
          "<p>Emplois BNSSA &amp; MNS agit en tant qu'intermédiaire et ne peut être tenu responsable des relations contractuelles entre candidats et recruteurs.</p>",
      },
      {
        title: "Droit applicable",
        content:
          "<p>Les présentes CGU/CGV sont régies par le droit français. Tout litige relèvera des tribunaux compétents de Paris.</p>",
      },
    ],
  },
};

/** Construit le BreadcrumbList JSON-LD pour une page légale */
function buildBreadcrumb(h1: string, canonical: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://bnssa-mns.fr/",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": h1,
        "item": canonical,
      },
    ],
  };
}

export default function LegalPage({ type }: LegalProps) {
  // Normalise les alias hérités vers les clés CONTENT
  const key: LegalType =
    (type as string) === "mentions" ? "mentions-legales"
    : (type as string) === "confidentialite" ? "politique-confidentialite"
    : (type as string) === "cgu" ? "cgv"
    : (type as LegalType);

  const staticData = STATIC_CONTENT[key];

  // Récupère un éventuel override admin via la collection `legal_pages`
  const { getByKey } = useLegalPages();
  const override = getByKey(key as LegalPageKey);

  const data: LegalContent = useMemo(() => {
    if (!staticData) {
      // Garde-fou : si la clé est inconnue mais qu'un override existe, on l'utilise
      if (override) {
        return {
          title: override.title,
          h1: override.h1,
          description: override.metaDescription,
          canonical: `https://bnssa-mns.fr/${key}`,
          sections: override.sections,
        };
      }
      return null as unknown as LegalContent;
    }
    if (!override) return staticData;
    return {
      title: override.title || staticData.title,
      h1: override.h1 || staticData.h1,
      description: override.metaDescription || staticData.description,
      canonical: staticData.canonical,
      sections:
        Array.isArray(override.sections) && override.sections.length > 0
          ? override.sections
          : staticData.sections,
    };
  }, [staticData, override, key]);

  if (!data) {
    return null;
  }

  const breadcrumbJsonLd = buildBreadcrumb(data.h1, data.canonical);

  return (
    <Layout>
      <Seo
        title={data.title}
        description={data.description}
        canonical={data.canonical}
        jsonLd={breadcrumbJsonLd}
      />

      <section className="bg-gray-50 border-b border-gray-200 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* BreadcrumbList visible */}
          <nav aria-label="Fil d'Ariane" className="mb-4">
            <ol className="flex items-center gap-2 text-sm text-gray-400">
              <li>
                <a href="/" className="hover:text-gray-600 transition-colors">
                  Accueil
                </a>
              </li>
              <li aria-hidden="true" className="text-gray-300">›</li>
              <li className="text-gray-700 font-medium">{data.h1}</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{data.h1}</h1>
          <p className="text-sm text-gray-500">
            Dernière mise à jour :{" "}
            {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            {data.sections.map((s, i) => (
              <section key={i} className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-3">
                  {i + 1}. {s.title}
                </h2>
                <div
                  className="text-gray-700 leading-relaxed prose-seo [&_p]:mb-3 [&_a]:text-blue-600 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-bold [&_h3]:mt-3 [&_h3]:mb-2 [&_strong]:font-semibold [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:italic"
                  dangerouslySetInnerHTML={{ __html: s.content }}
                />
              </section>
            ))}
          </article>
        </div>
      </section>
    </Layout>
  );
}
